import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowRight, ShieldCheck, Sparkles, BookOpenCheck } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
    const { login, user } = useAuth();
    const navigate = useNavigate();
    const [username, setUsername] = useState("demo");
    const [password, setPassword] = useState("demo123");
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (user) navigate("/", { replace: true });
    }, [user, navigate]);

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            await login(username, password);
            toast.success("Welcome to Vyaahar");
            navigate("/", { replace: true });
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Login failed");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="min-h-screen grid lg:grid-cols-2 bg-background">
            {/* Left: brand panel */}
            <div className="hidden lg:flex relative flex-col justify-between p-12 overflow-hidden" style={{ background: "hsl(222 32% 11%)", color: "hsl(39 30% 88%)" }}>
                <div className="absolute inset-0 opacity-[0.06]" style={{
                    backgroundImage: "radial-gradient(circle at 20% 30%, hsl(39 50% 60%) 0%, transparent 35%), radial-gradient(circle at 80% 70%, hsl(215 50% 50%) 0%, transparent 40%)",
                }} />
                <div className="absolute inset-0" style={{
                    backgroundImage: "linear-gradient(hsl(39 50% 50% / 0.04) 1px, transparent 1px), linear-gradient(90deg, hsl(39 50% 50% / 0.04) 1px, transparent 1px)",
                    backgroundSize: "32px 32px",
                }} />

                <div className="relative z-10">
                    <img src="/vyaahar-logo.png" alt="Vyaahar by Vyastics Technologies Private Limited" className="h-12 object-contain object-left" />
                </div>

                <div className="relative z-10 max-w-lg">
                    <h2 className="font-display font-light leading-[1.05] tracking-tight" style={{ fontSize: "44px" }}>
                        Finance, books, and compliance
                        <span className="block italic mt-1" style={{ color: "hsl(39 50% 65%)" }}>in one system.</span>
                    </h2>
                    <p className="text-[14px] mt-5 leading-relaxed" style={{ color: "hsl(220 10% 70%)" }}>
                        A serious accounting platform for serious finance teams.
                        Multi-currency double-entry, GST &amp; e-invoicing, deep reports, and an AI copilot —
                        all designed for the way Indian businesses actually run.
                    </p>
                    <div className="grid grid-cols-3 gap-3 mt-8">
                        {[
                            { Icon: BookOpenCheck, label: "Double-entry engine" },
                            { Icon: ShieldCheck, label: "GST &amp; E-invoicing" },
                            { Icon: Sparkles, label: "AI Copilot" },
                        ].map((f, i) => (
                            <div key={i} className="p-3 backdrop-blur-sm" style={{ background: "hsl(0 0% 100% / 0.04)", border: "1px solid hsl(0 0% 100% / 0.08)", borderRadius: 2 }}>
                                <f.Icon className="w-4 h-4 mb-2" strokeWidth={1.5} style={{ color: "hsl(39 50% 65%)" }} />
                                <div className="text-[11px]" style={{ color: "hsl(220 10% 78%)" }} dangerouslySetInnerHTML={{ __html: f.label }} />
                            </div>
                        ))}
                    </div>
                </div>

                <div className="relative z-10 text-[11px]" style={{ color: "hsl(220 10% 55%)" }}>
                    © {new Date().getFullYear()} Vyastics Technologies Private Limited — engineered for finance teams who care about the details.
                </div>
            </div>

            {/* Mobile: dark brand band at top with centered logo */}
            <div className="lg:hidden relative w-full overflow-hidden" style={{ background: "hsl(222 32% 11%)" }}>
                <div className="absolute inset-0 opacity-[0.06]" style={{
                    backgroundImage: "radial-gradient(circle at 20% 30%, hsl(39 50% 60%) 0%, transparent 35%), radial-gradient(circle at 80% 70%, hsl(215 50% 50%) 0%, transparent 40%)",
                }} />
                <div className="absolute inset-0" style={{
                    backgroundImage: "linear-gradient(hsl(39 50% 50% / 0.04) 1px, transparent 1px), linear-gradient(90deg, hsl(39 50% 50% / 0.04) 1px, transparent 1px)",
                    backgroundSize: "32px 32px",
                }} />
                <div className="relative z-10 flex justify-center py-8">
                    <img src="/vyaahar-logo.png" alt="Vyaahar" className="h-10 object-contain" />
                </div>
            </div>

            {/* Right: form */}
            <div className="flex flex-col items-center justify-center p-6 sm:p-12">
                <div className="w-full max-w-[400px]">
                    <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] font-mono mb-3">
                        <span className="text-gold">SIGN IN</span>
                        <span className="opacity-40">·</span>
                        <span className="text-muted-foreground">{new Date().toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }).toUpperCase()}</span>
                        <span className="live-dot ml-1" />
                    </div>
                    <h1 className="font-display text-[40px] font-light tracking-tight text-foreground leading-[1.04]">
                        Welcome <span className="italic" style={{ color: "hsl(var(--gold))" }}>back.</span>
                    </h1>
                    <p className="text-[12.5px] text-muted-foreground mt-3 font-mono">
                        <span className="text-gold mr-1.5">$</span>vyaahar login --workspace
                    </p>

                    <form onSubmit={submit} className="mt-8 space-y-4" data-testid="login-form">
                        <div>
                            <Label htmlFor="username" className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground font-mono">USERNAME / EMAIL</Label>
                            <Input
                                id="username"
                                data-testid="login-username"
                                value={username}
                                onChange={(e) => setUsername(e.target.value)}
                                placeholder="demo"
                                className="mt-1.5 h-11 font-mono"
                                style={{ borderRadius: 2 }}
                                autoComplete="username"
                            />
                        </div>
                        <div>
                            <Label htmlFor="password" className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground font-mono">PASSWORD</Label>
                            <Input
                                id="password"
                                data-testid="login-password"
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="••••••••"
                                className="mt-1.5 h-11 font-mono"
                                style={{ borderRadius: 2 }}
                                autoComplete="current-password"
                            />
                        </div>
                        <Button type="submit" disabled={busy} className="w-full h-11 mt-2 group font-semibold" style={{ borderRadius: 2 }} data-testid="login-submit">
                            {busy ? "AUTHENTICATING…" : "SIGN IN"}
                            <ArrowRight className="w-4 h-4 ml-1.5 group-hover:translate-x-0.5 transition-transform" />
                        </Button>
                        <div className="border border-gold/40 bg-gold-soft/60 px-3.5 py-2.5 text-[11.5px] text-foreground/80 font-mono" style={{ borderRadius: 2 }}>
                            <span className="text-gold mr-1">{">"}</span><span className="text-gold font-semibold">demo:</span> demo / demo123 — pre-seeded with 24mo of realistic Indian SME data.
                        </div>
                    </form>

                    <div className="mt-6 text-center text-[12px] text-muted-foreground font-mono">
                        New to Vyaahar? <Link to="/signup" className="text-gold font-semibold hover:underline" data-testid="link-signup">create-workspace →</Link>
                    </div>

                    <div className="mt-12 text-[10.5px] text-muted-foreground font-mono leading-relaxed">
                        Vyaahar by Vyastics Technologies Private Limited.<br/>
                        Encrypted in transit · Encrypted at rest · GST &amp; e-invoicing ready.
                    </div>
                </div>
            </div>
        </div>
    );
}
